import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import {
  GtmEcommerceData,
  GtmEventOptions,
  GtmPayload,
} from "../models/gtm-ecommerce.model";
import { environment } from "../../../../environments/environment";

/**
 * Google Tag Manager – wyłącznie dla publicznego kreatora (`/creator/*`).
 *
 * Skrypt kontenera NIE jest w `index.html`: wstrzykuje go `load()`, wołane
 * przez `loadGtmGuard` przy wejściu na trasę `/creator`. Panel, logowanie
 * i reset hasła są chronione przez `gtmFreeGuard` – jeśli GTM został już
 * wczytany (przejście z kreatora), wymuszane jest pełne przeładowanie strony,
 * bo raz wczytanego skryptu GTM nie da się z pamięci usunąć.
 *
 * Wszystkie operacje na obiekcie `window` są chronione przez `isPlatformBrowser`,
 * żeby SSR / prerendering nie próbował mutować globalu, którego tam nie ma.
 */

declare global {
  interface Window {
    dataLayer: Array<GtmPayload | { ecommerce: null } | Record<string, unknown>>;
  }
}

export const GTM_CONTAINER_ID = "GTM-5MP3SBJ";

/** Czy adres należy do kreatora – jedynej części aplikacji z GTM. */
export function isCreatorUrl(url: string): boolean {
  return /^\/creator(?:[/?#]|$)/.test(url);
}

@Injectable({ providedIn: "root" })
export class GoogleTagManagerService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly document = inject(DOCUMENT);
  private loaded = false;

  /** Czy skrypt kontenera GTM został wstrzyknięty w bieżącym załadowaniu strony. */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Wstrzykuje skrypt kontenera GTM (idempotentne). Odpowiednik standardowego
   * snippetu z `<head>`: najpierw zdarzenie `gtm.js` w dataLayer, potem
   * asynchroniczny `<script>`.
   *
   * W środowisku `development` (`environment.gtmEnabled = false`) nic nie
   * robi – `isLoaded()` zostaje `false`, więc `gtmFreeGuard` nie przeładowuje
   * strony, a zdarzenia z serwisu trafiają tylko do lokalnej `dataLayer`.
   */
  load(): void {
    if (!environment.gtmEnabled || !this.isBrowser || this.loaded) return;
    this.loaded = true;

    this.getDataLayer().push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const script = this.document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
    this.document.head.appendChild(script);
  }

  /** Pełne przeładowanie strony pod adresem `url` – usuwa GTM z pamięci. */
  reloadPageAt(url: string): void {
    if (!this.isBrowser) return;
    this.document.location.assign(url);
  }

  /** Wirtualna odsłona strony w aplikacji SPA – wołane z `NavigationEnd`. */
  pushPageView(url: string): void {
    this.push({
      event: "virtual_page_view",
      page_path: url,
    });
  }

  /**
   * Zdarzenie e-commerce zgodne z rekomendacją GA4. Standardowa procedura Google:
   * przed właściwym push-em wysyłamy `ecommerce: null`, żeby zresetować poprzedni
   * kontekst i uniknąć nakładania się danych z wcześniejszego zdarzenia.
   *
   * `user_data` i `event_id` trafiają na poziom `ecommerce` (nie do jego
   * wnętrza). `callback` (np. przekierowanie do bramki) wołany jest raz – po
   * obsłużeniu zdarzenia przez GTM (`eventCallback`) albo po limicie czasu,
   * także gdy GTM się nie wczytał (adblock, `gtmEnabled = false`).
   */
  pushEcommerceEvent(
    eventName: string,
    data: GtmEcommerceData,
    options: GtmEventOptions = {},
  ): void {
    if (!this.isBrowser) {
      options.callback?.();
      return;
    }
    const layer = this.getDataLayer();
    layer.push({ ecommerce: null });
    const payload: GtmPayload = {
      event: eventName,
      ...(options.eventId ? { event_id: options.eventId } : {}),
      ecommerce: data,
      ...(options.userData ? { user_data: options.userData } : {}),
    };

    if (options.callback) {
      const callback = options.callback;
      const timeoutMs = options.timeoutMs ?? 800;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        callback();
      };
      payload.eventCallback = finish;
      payload.eventTimeout = timeoutMs;
      setTimeout(finish, timeoutMs + 200);
    }
    layer.push(payload);
  }

  private push(payload: Record<string, unknown>): void {
    if (!this.isBrowser) return;
    this.getDataLayer().push(payload);
  }

  private getDataLayer(): Window["dataLayer"] {
    if (!Array.isArray(window.dataLayer)) {
      // dataLayer tworzymy leniwie – nie ma go już w index.html. Ten sam
      // fallback zabezpiecza scenariusz, w którym rozszerzenie przeglądarki
      // wyczyściło globalną tablicę.
      window.dataLayer = [];
    }
    return window.dataLayer;
  }
}
